/**
 * Combat on the grid — M2-01, M2-02 and M2-03, which are one machine.
 *
 * The structural decision the AoD doc could not make for us (decision log #1):
 * **every attack is a player action**. AoD is a crew game where a fight is a
 * sequence of exchanges the system runs; Annwn is personal, and the attacker's
 * choices come from a human, per swing. So the exchange machinery of AoD §7.4
 * survives whole — contested dice, a pool split, a margin into the M1-03
 * pipeline — but there is no exchange *loop*. There is a tick, and on that tick
 * some people's swings happen to land.
 *
 * What replaces the loop is timing. An attack is an intent that **commits**:
 * wind-up ticks pass, the blow resolves, recovery ticks pass. That is what buys
 * counterplay without twitch — a dane axe telegraphs for eight ticks, and an
 * opponent has most of a second to read it and raise a guard, which is a
 * decision made at tick granularity and therefore fair at 150 ms of latency.
 * It is also why movement during a wind-up cancels the strike: footwork and
 * commitment are the two things a fighter trades between, and a game where you
 * could step *and* swing has neither.
 *
 * Nothing here rolls its own damage. A contested margin goes into
 * `resolveDamage` and everything after that — location, armour coverage, wound
 * tables, shock, bleeding, narration — is M1's, unchanged. That seam is why a
 * wolf's bite, a spear thrust and a fall down a crag all produce the same kind
 * of wound record.
 *
 * State lives in `fighter.ts`; this is the rules over it.
 */
import { chebyshev, DIRECTION_VECTORS, DIRECTIONS, } from './actor.js';
import { parseAttack, parseDisengage, parseEngage, parseGuard, } from './actions.js';
import { toughnessRating } from './body.js';
import { resolveDamage, weaponDamageRating } from './damage.js';
import { rollSuccesses } from './exchange.js';
import { bandAt, combatDice, defenceShare, GUARD_REGEN_FACTOR, isCommitted, kitOf, maxStamina, SHIELD_BROKEN, shieldConditionAt, shieldDice, shieldOf, handsFull, shieldWearChance, spendStamina, STAMINA_REGEN, STAMINA_SCALE, STAND_COST, STAND_TICKS, staminaFraction, } from './fighter.js';
import { footingAt, isPassable } from './map.js';
import { actorAt } from './movement.js';
import { hasLineOfSight } from './senses.js';
import { armourAt, narrateBlow } from './narrate.js';
import { strainOnExertion } from './recovery.js';
import { breakStalk } from './stalk.js';
import { advanceClocks, isDown, sufferBlow } from './vitals.js';
/* -------------------------------------------------------------------------- *
 * Constants (M2-02)
 * -------------------------------------------------------------------------- */
/** Defence dice multiplier by which side of the defender the blow comes from. */
export const FACING_DEFENCE = { front: 1, flank: 0.5, rear: 0.25 };
/** Ticks over which defences accumulate against a defender. */
export const DEFENCE_WINDOW = 10;
/** Dice lost per earlier defence inside the window. */
export const DEFENCE_DEGRADATION = 1;
/** How many can crowd one defender at once. The rest circle, seeking flanks. */
export const ENGAGEMENT_CAP = 3;
/** Dice a clean defensive win is worth on the reply (TRoS's riposte rhythm). */
export const RIPOSTE_DICE = 2;
/** How long that reply stays available. Two seconds; a rhythm, not a buff. */
export const RIPOSTE_TICKS = 20;
/** Fighting from the ground. Not a death sentence, but close enough to hurt. */
export const PRONE_ATTACK_PENALTY = 3;
export const PRONE_DEFENCE_FACTOR = 0.5;
/** How long a buffered action waits for its moment before it is forgotten. */
export const QUEUE_TICKS = 12;
/** AoD §7.6: breaking off costs a round of exposure, not a free sidestep. */
export const DISENGAGE_TICKS = 8;
/**
 * What placing a blow costs in to-hit dice, by how big the target is (M2-03).
 *
 * The eye and the throat are worth −5 because they should be a thing a
 * desperate or a very good fighter does once, not a rotation. They stay
 * *possible*, because the whole argument for direct control is that the player
 * gets to decide what to risk.
 */
export const AIM_PENALTY = {
    chest: 1,
    abdomen: 1,
    arm: 2,
    leg: 2,
    foot: 2,
    hand: 3,
    head: 3,
    neck: 5,
    eye: 5,
};
/** A placed blow is a committed blow. */
export const AIM_WINDUP = 1;
export function aimPenaltyFor(plan, part) {
    const def = plan.parts.find((candidate) => candidate.id === part);
    if (!def)
        return 0;
    return AIM_PENALTY[def.zone];
}
/* -------------------------------------------------------------------------- *
 * Position (M2-02 facing, M2-03 reach)
 * -------------------------------------------------------------------------- */
/**
 * The compass direction an offset points in, snapped to the nearest of eight.
 *
 * Integer comparisons rather than `atan2`: a facing that came out of a float
 * would be a facing that could disagree between two machines on the tile where
 * it rounds, and a flank is worth half the defence dice.
 */
export function directionToward(dx, dy) {
    if (dx === 0 && dy === 0)
        return null;
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    const ns = dy < 0 ? 'n' : dy > 0 ? 's' : '';
    const ew = dx > 0 ? 'e' : dx < 0 ? 'w' : '';
    if (ns && ew) {
        // A diagonal only when the minor axis is at least half the major;
        // otherwise it is a cardinal with a lean.
        if (ax * 2 < ay)
            return ns;
        if (ay * 2 < ax)
            return ew;
        return `${ns}${ew}`;
    }
    return (ns || ew);
}
/**
 * Which side of the defender a blow arrives on.
 *
 * Facing follows action — the last step or swing — rather than being something
 * players micro-manage. That is decision log #1 applied to the other end of the
 * exchange: a positioning game, not a facing game.
 */
export function facingOf(defender, fromX, fromY) {
    const toward = directionToward(fromX - defender.x, fromY - defender.y);
    if (!toward)
        return 'front';
    const a = DIRECTIONS.indexOf(defender.facing);
    const b = DIRECTIONS.indexOf(toward);
    const delta = Math.min(Math.abs(a - b), DIRECTIONS.length - Math.abs(a - b));
    if (delta <= 1)
        return 'front';
    if (delta <= 3)
        return 'flank';
    return 'rear';
}
/**
 * A tile with no room to get round: walls on both sides of one axis.
 *
 * The doorway of the milestone's exit criteria, derived from the terrain rather
 * than declared by a tile kind — so a hut door and a gap between two crags are
 * the same tactical fact, which is what a player standing in one expects. M3
 * consumes this properly; the hook is here because M2-02 needs it to make "the
 * doorway restores near-parity" a test rather than a promise.
 */
export function isChokepoint(map, x, y) {
    const blocked = (dx, dy) => !isPassable(map, x + dx, y + dy);
    return (blocked(-1, 0) && blocked(1, 0)) || (blocked(0, -1) && blocked(0, 1));
}
/**
 * Can a reach-2 thrust get through? There has to be somewhere for the shaft to
 * pass: at least one tile adjacent to both ends that is neither wall nor ally.
 *
 * No friendly skewering in the MVP (M2-03), and no thrusting through a hut
 * wall. Both fall out of the same question, which is why they are one function.
 */
export function reachPathClear(world, fromX, fromY, toX, toY) {
    if (chebyshev(fromX, fromY, toX, toY) <= 1)
        return true;
    for (let y = Math.min(fromY, toY); y <= Math.max(fromY, toY); y += 1) {
        for (let x = Math.min(fromX, toX); x <= Math.max(fromX, toX); x += 1) {
            if (x === fromX && y === fromY)
                continue;
            if (x === toX && y === toY)
                continue;
            if (chebyshev(x, y, fromX, fromY) !== 1)
                continue;
            if (chebyshev(x, y, toX, toY) !== 1)
                continue;
            if (!isPassable(world.map, x, y))
                continue;
            if (actorAt(world, x, y))
                continue;
            return true;
        }
    }
    return false;
}
/* -------------------------------------------------------------------------- *
 * Legality
 * -------------------------------------------------------------------------- */
/** The actor an attack request names — by id, or by the tile it bumps into. */
export function resolveTarget(world, attacker, request) {
    if (request.targetId !== null)
        return world.actors[request.targetId] ?? null;
    if (request.direction === null)
        return null;
    const [dx, dy] = DIRECTION_VECTORS[request.direction];
    return actorAt(world, attacker.x + dx, attacker.y + dy, attacker.id) ?? null;
}
/** How many people are already swinging at this actor. */
export function attackersOn(world, targetId) {
    let count = 0;
    for (const [id, combatant] of Object.entries(world.combat)) {
        if (id === targetId)
            continue;
        if (combatant.swing?.targetId === targetId)
            count += 1;
    }
    return count;
}
/** People who have deliberately entered an exchange with this target. */
export function engagedAttackers(world, targetId) {
    return Object.entries(world.combat)
        .filter(([id, combatant]) => id !== targetId && combatant.engagedWith === targetId)
        .map(([id]) => id);
}
/** An exchange holds you in place whether you started it or someone else did. */
export function isEngaged(world, actorId) {
    const combatant = world.combat[actorId];
    return ((combatant?.engagedWith !== null && combatant?.engagedWith !== undefined) ||
        engagedAttackers(world, actorId).length > 0);
}
/**
 * Why this attack cannot happen, or `null`. Shared by the intent's `check` and
 * by the strike itself, because a queued swing has to be judged again at the
 * tick it executes and two copies of this rule would have drifted by M3.
 *
 * `capped` is false at the strike, and only there. The three-attacker limit is
 * a rule about *closing* — you cannot find room to get at someone three people
 * are already on — and applying it again a wind-up later would delete a blow
 * that was legal when it was thrown, because two strangers stepped in behind it.
 * `ammunitionCommitted` is likewise true only for a resolving ranged strike:
 * the arrow was paid at commitment and must not be demanded a second time.
 */
export function attackFault(world, catalogue, attacker, request, tick, capped = true, ammunitionCommitted = false) {
    const combatant = world.combat[attacker.id];
    if (!combatant)
        return `${attacker.id} has no body to fight with`;
    const kit = kitOf(catalogue, combatant);
    if (!kit)
        return `${attacker.id} carries content this world does not have`;
    if (combatant.vitals.dead)
        return `${attacker.id} is dead`;
    if (isDown(kit.plan, combatant.vitals, tick))
        return `${attacker.id} is senseless`;
    // Both arms are full of somebody (M4-06). The carrier's whole decision is
    // whether to put them down and fight, and a carrier who could do both would
    // have made that decision for free.
    if (handsFull(combatant))
        return 'you cannot fight with somebody in your arms';
    const target = resolveTarget(world, attacker, request);
    if (!target)
        return 'there is nothing there to hit';
    if (target.id === attacker.id)
        return 'nobody swings at themselves';
    const defender = world.combat[target.id];
    if (!defender)
        return `${target.name} cannot be fought`;
    const ranged = kit.weapon.ranged;
    if (ranged && !ammunitionCommitted && (combatant.loadout.ammunition ?? 0) < 1) {
        return 'you have no arrows';
    }
    if (!ranged && combatant.engagedWith !== target.id) {
        return `you are not engaged with ${target.name}`;
    }
    const distance = chebyshev(attacker.x, attacker.y, target.x, target.y);
    if (distance < 1)
        return 'too close to swing';
    const reach = ranged?.range ?? kit.timing.reach;
    if (distance > reach)
        return `${target.name} is out of ${ranged ? 'range' : 'reach'}`;
    if (ranged && !hasLineOfSight(world.map, attacker.x, attacker.y, target.x, target.y)) {
        return 'the line of fire is blocked';
    }
    if (!ranged && !reachPathClear(world, attacker.x, attacker.y, target.x, target.y)) {
        return 'the way is blocked';
    }
    if (request.part !== null) {
        const defenderKit = kitOf(catalogue, defender);
        if (!defenderKit?.plan.parts.some((part) => part.id === request.part)) {
            return `there is no '${request.part}' to aim at`;
        }
    }
    if (capped && attackersOn(world, target.id) >= ENGAGEMENT_CAP) {
        return `there is no room to get at ${target.name}`;
    }
    return null;
}
/* -------------------------------------------------------------------------- *
 * Intents (M2-01)
 * -------------------------------------------------------------------------- */
/** Turn an accepted request into a committed swing, and pay for it. */
function commit(world, attacker, combatant, kit, request, tick) {
    const band = bandAt(staminaFraction(combatant, kit));
    const windUp = kit.timing.windUp + band.windUp + (request.part === null ? 0 : AIM_WINDUP);
    spendStamina(combatant, kit, kit.timing.staminaCost);
    // Facing follows action: you are looking at what you are swinging at, and
    // that is what decides who is flanking you a moment later.
    const target = resolveTarget(world, attacker, request);
    const toward = target
        ? directionToward(target.x - attacker.x, target.y - attacker.y)
        : request.direction;
    if (toward)
        attacker.facing = toward;
    const swing = {
        targetId: request.targetId,
        direction: request.direction,
        part: request.part,
        fromX: attacker.x,
        fromY: attacker.y,
        strikeAt: tick + windUp,
        recovery: kit.timing.recovery,
    };
    combatant.swing = swing;
    return swing;
}
function emitCommitted(ctx, attacker, kit, swing, extra = []) {
    ctx.emit({
        type: 'attacked',
        actors: [attacker.id, ...(swing.targetId === null ? [] : [swing.targetId])],
        refs: [kit.weapon.id, String(swing.strikeAt), swing.part ?? '', ...extra],
        // Archived, not narrated. The *reading* of a wind-up is the opponent's
        // glyph on the client (M2-06), not a line of prose forty times a fight.
        audience: 'archive',
        at: { x: attacker.x, y: attacker.y },
    });
}
export const attackHandler = {
    type: 'attack',
    parse: parseAttack,
    check(intent, ctx) {
        const attacker = ctx.world.actors[intent.actorId];
        if (!attacker)
            return `${intent.actorId} is not in the world`;
        const combatant = ctx.world.combat[intent.actorId];
        if (!combatant)
            return `${intent.actorId} has no body to fight with`;
        if (isCommitted(combatant, ctx.tick)) {
            return 'already committed to an exchange';
        }
        return attackFault(ctx.world, ctx.catalogue, attacker, intent.payload, ctx.tick);
    },
    apply(intent, ctx) {
        const attacker = ctx.world.actors[intent.actorId];
        const combatant = ctx.world.combat[intent.actorId];
        if (!attacker || !combatant)
            return;
        if (isCommitted(combatant, ctx.tick))
            return;
        const kit = kitOf(ctx.catalogue, combatant);
        if (!kit)
            return;
        if (kit.weapon.ranged) {
            combatant.loadout = {
                ...combatant.loadout,
                ammunition: Math.max(0, (combatant.loadout.ammunition ?? 0) - 1),
            };
        }
        // A swing is loud (M5-03). The stalk ends the moment you commit to one,
        // whether or not it lands — which is what makes the ambush a *single*
        // decision rather than a stance you fight from.
        breakStalk(ctx, attacker.id, combatant, 'swung');
        emitCommitted(ctx, attacker, kit, commit(ctx.world, attacker, combatant, kit, intent.payload, ctx.tick));
        // The exertion burst (M4-04). Rolled on the *commit* rather than on the
        // blow landing, because what tears a half-healed wound open is throwing your
        // weight behind a swing, and that happens whether or not it connects.
        strainOnExertion(ctx, attacker);
    },
};
/** Enter an exchange with a target that the held melee weapon can reach. */
export const engageHandler = {
    type: 'engage',
    parse: parseEngage,
    check(intent, ctx) {
        const attacker = ctx.world.actors[intent.actorId];
        if (!attacker)
            return `${intent.actorId} is not in the world`;
        const combatant = ctx.world.combat[attacker.id];
        if (!combatant)
            return `${attacker.id} has no body to fight with`;
        if (combatant.engagedWith !== null || combatant.withdrawingUntil > ctx.tick) {
            return 'already engaged or breaking off';
        }
        const target = resolveTarget(ctx.world, attacker, { ...intent.payload, part: null });
        if (!target || target.id === attacker.id)
            return 'there is nobody there to engage';
        const defender = ctx.world.combat[target.id];
        if (!defender)
            return `${target.name} cannot be fought`;
        const kit = kitOf(ctx.catalogue, combatant);
        if (!kit)
            return `${attacker.name} cannot fight`;
        const distance = chebyshev(attacker.x, attacker.y, target.x, target.y);
        if (distance < 1 || distance > kit.timing.reach) {
            return `${target.name} is not close enough to engage`;
        }
        if (!reachPathClear(ctx.world, attacker.x, attacker.y, target.x, target.y)) {
            return 'the way is blocked';
        }
        if (engagedAttackers(ctx.world, target.id).length >= ENGAGEMENT_CAP) {
            return `${target.name} is already surrounded`;
        }
        return null;
    },
    apply(intent, ctx) {
        const attacker = ctx.world.actors[intent.actorId];
        if (!attacker)
            return;
        const target = resolveTarget(ctx.world, attacker, { ...intent.payload, part: null });
        const combatant = ctx.world.combat[attacker.id];
        const defender = target ? ctx.world.combat[target.id] : undefined;
        if (!target || !combatant || !defender)
            return;
        combatant.engagedWith = target.id;
        // A defender keeps one primary opponent for their own attack input, but
        // several people may commit to them. Each attacker's declaration is
        // authoritative; otherwise a second attacker overwrites the first and the
        // advertised three-person crowd silently becomes impossible.
        if (defender.engagedWith === null)
            defender.engagedWith = attacker.id;
        ctx.emit({
            type: 'status',
            actors: [attacker.id, target.id],
            refs: ['engaged'],
            text: `${attacker.name} squares up to ${target.name}.`,
            at: { x: attacker.x, y: attacker.y },
        });
    },
};
/** Begin an exposed break-off. It is available even while a blow is winding. */
export const disengageHandler = {
    type: 'disengage',
    parse: parseDisengage,
    check(intent, ctx) {
        const combatant = ctx.world.combat[intent.actorId];
        if (!combatant)
            return `${intent.actorId} has no body to fight with`;
        if (!isEngaged(ctx.world, intent.actorId))
            return 'you are not engaged';
        if (combatant.withdrawingUntil > ctx.tick)
            return 'already breaking off';
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const combatant = ctx.world.combat[intent.actorId];
        if (!actor || !combatant)
            return;
        const opponentId = combatant.engagedWith ?? engagedAttackers(ctx.world, actor.id)[0] ?? null;
        if (opponentId === null)
            return;
        combatant.swing = null;
        combatant.queued = null;
        combatant.withdrawingUntil = ctx.tick + DISENGAGE_TICKS;
        ctx.emit({
            type: 'status',
            actors: [actor.id, opponentId],
            refs: ['break-off', String(combatant.withdrawingUntil)],
            text: `${actor.name} tries to break off.`,
            at: { x: actor.x, y: actor.y },
        });
    },
};
export const guardHandler = {
    type: 'guard',
    parse: parseGuard,
    check(intent, ctx) {
        if (!ctx.world.actors[intent.actorId])
            return `${intent.actorId} is not in the world`;
        const combatant = ctx.world.combat[intent.actorId];
        if (!combatant)
            return `${intent.actorId} has no body to fight with`;
        if (combatant.vitals.dead)
            return `${intent.actorId} is dead`;
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const combatant = ctx.world.combat[intent.actorId];
        if (!actor || !combatant)
            return;
        if (combatant.guarding === intent.payload.on)
            return;
        combatant.guarding = intent.payload.on;
        ctx.emit({
            type: 'guarded',
            actors: [actor.id],
            refs: [intent.payload.on ? 'up' : 'down'],
            audience: 'archive',
            at: { x: actor.x, y: actor.y },
        });
    },
};
export const COMBAT_INTENTS = [
    engageHandler,
    disengageHandler,
    attackHandler,
    guardHandler,
];
/**
 * The dice both sides bring to one blow.
 *
 * Pulled out of the resolution so a scenario test can ask *what would this
 * exchange look like* without rolling it — which is how the guard, flanking and
 * doorway criteria get asserted as numbers instead of as win rates over a
 * thousand runs.
 */
export function exchangeDice(world, attacker, attackerKit, defender, defenderKit, swing, tick) {
    const attackerC = world.combat[attacker.id];
    const defenderC = world.combat[defender.id];
    let attack = Math.floor(combatDice(attackerC, attackerKit) * (1 - defenceShare(attackerC, tick)));
    // Inside your own reach is the spearman's problem, and the reason the
    // step-back-and-thrust rhythm exists without a mechanic for it.
    const distance = chebyshev(attacker.x, attacker.y, defender.x, defender.y);
    if (attackerKit.weapon.ranged) {
        attack -= Math.max(0, distance - 1) * attackerKit.weapon.ranged.falloffDice;
    }
    if (distance < attackerKit.timing.reach)
        attack -= attackerKit.timing.insidePenalty;
    if (swing.part !== null)
        attack -= aimPenaltyFor(defenderKit.plan, swing.part);
    if (attackerC.vitals.prone)
        attack -= PRONE_ATTACK_PENALTY;
    if (attackerC.riposteAgainst === defender.id && tick < attackerC.riposteUntil) {
        attack += RIPOSTE_DICE;
    }
    const chokepoint = isChokepoint(world.map, defender.x, defender.y);
    const facing = chokepoint ? 'front' : facingOf(defender, attacker.x, attacker.y);
    let defence = Math.floor(combatDice(defenderC, defenderKit) * defenceShare(defenderC, tick));
    // The board and the stave: both are things held up in front of you, and
    // neither is any help against a blow that arrives from behind.
    if (facing !== 'rear') {
        defence += shieldDice(defenderKit.armour, defenderC.shieldWear);
        defence += defenderKit.weapon.defenceDice ?? 0;
    }
    defence = Math.floor(defence * FACING_DEFENCE[facing]);
    if (defenderC.vitals.prone)
        defence = Math.floor(defence * PRONE_DEFENCE_FACTOR);
    // The ground the defender is standing on (M3-01). Applied to defence alone
    // and not to the attack, because a bog does not stop you swinging — it stops
    // you getting out of the way, and a fight in the river should be a thing you
    // chose or a thing you were driven into.
    const footing = footingAt(world.map, defender.x, defender.y);
    if (footing !== 1)
        defence = Math.floor(defence * footing);
    const degraded = recentDefences(defenderC, tick) * DEFENCE_DEGRADATION;
    defence -= degraded;
    return {
        attack: Math.max(0, attack),
        defence: Math.max(0, defence),
        facing,
        degraded,
        chokepoint,
        footing,
    };
}
function recentDefences(combatant, tick) {
    let count = 0;
    for (const when of combatant.defences) {
        if (tick - when < DEFENCE_WINDOW)
            count += 1;
    }
    return count;
}
function pruneDefences(combatant, tick) {
    combatant.defences = combatant.defences.filter((when) => tick - when < DEFENCE_WINDOW);
}
function party(actor, combatant) {
    return { id: actor.id, name: actor.name, pronouns: combatant.pronouns };
}
/**
 * How a losing defender stopped it. Cosmetic in mechanics, load-bearing in the
 * log (AoD §12.1: name the thing that stopped the blow). A raised guard mostly
 * parries; a blow from behind that somehow misses was footwork and luck.
 */
function defenceKind(rng, defender, dice) {
    if (dice.facing === 'rear')
        return 'dodge';
    if (defender.guarding)
        return rng.chance(0.7) ? 'parry' : 'give-ground';
    return rng.chance(0.5) ? 'parry' : 'dodge';
}
/**
 * A parry that a shield was part of costs the shield something, sometimes
 * (M2-04).
 *
 * Charged on the parry rather than on every blow the defender survives, because
 * a parry is the one defence that means *the blow was met with something*: a
 * dodge and a give-ground are the fighter's feet, and neither of them puts an
 * axe into limewood. A blow from behind never reaches the board at all, which
 * is the same rule that took its dice away in `exchangeDice`.
 *
 * The chance scales with the attacker's damage rating, so what breaks a shield
 * is the weapon that historically broke shields, and no counter has to be kept
 * to say so.
 */
function wearShield(rng, attackerC, attackerKit, defenderC, defenderKit, dice) {
    if (dice.facing === 'rear')
        return;
    if (!shieldOf(defenderKit.armour))
        return;
    // Asked of the *condition*, not of the dice it is still worth. A split board
    // is worth no dice at all and is still strapped to somebody's arm, and the
    // axe that finally takes it off has to be able to land.
    if (defenderC.shieldWear >= SHIELD_BROKEN)
        return;
    const rating = weaponDamageRating(attackerKit.weapon, attackerC.loadout.attributes, attackerC.loadout.edge);
    if (rng.chance(shieldWearChance(rating)))
        defenderC.shieldWear += 1;
}
/**
 * One blow, from the contested roll to the last clock event.
 *
 * The order of rolls is fixed and load-bearing, exactly as in M1's harness:
 * attack dice, then defence dice, then the pipeline's. A roll inserted ahead of
 * another shifts every subsequent draw of every replay.
 */
function resolveBlow(ctx, attacker, attackerKit, defender, defenderKit, swing) {
    const table = ctx.catalogue.woundTable;
    if (!table)
        return null;
    const attackerC = ctx.world.combat[attacker.id];
    const defenderC = ctx.world.combat[defender.id];
    const rng = ctx.rng('combat');
    const tick = ctx.tick;
    const dice = exchangeDice(ctx.world, attacker, attackerKit, defender, defenderKit, swing, tick);
    const attackRoll = rollSuccesses(rng, dice.attack);
    const defenceRoll = rollSuccesses(rng, dice.defence);
    const margin = attackRoll - defenceRoll;
    defenderC.defences.push(tick);
    pruneDefences(defenderC, tick);
    // Being swung at ends a slow action (M2-07) — here, where the defence is
    // spent, rather than below where the wound is dealt.
    //
    // The distinction matters: a man who *parried* still stopped bandaging to
    // parry. Cancelling only on damage would have made a competent defender
    // able to loot a body while a friend of the deceased hacked at him, so long
    // as none of it got through, which is not a fight anybody would recognise.
    defenderC.channel = null;
    const parties = {
        attacker: party(attacker, attackerC),
        defender: party(defender, defenderC),
        weapon: attackerKit.weapon.name,
        attack: attackerKit.profile.id,
    };
    // The riposte is spent whether it landed or not — it is a moment, not a
    // charge you keep until it is convenient.
    if (attackerC.riposteAgainst === defender.id)
        attackerC.riposteAgainst = null;
    if (margin <= 0) {
        // A clean defensive win — not merely a tie — buys the reply (AoD §7.4).
        if (margin < 0) {
            defenderC.riposteAgainst = attacker.id;
            defenderC.riposteUntil = tick + RIPOSTE_TICKS;
        }
        const stopped = defenceKind(rng, defenderC, dice);
        if (stopped === 'parry')
            wearShield(rng, attackerC, attackerKit, defenderC, defenderKit, dice);
        return { ...parties, defence: stopped, armour: null, damage: null, events: [] };
    }
    if (defenderC.riposteAgainst === attacker.id)
        defenderC.riposteAgainst = null;
    const provenance = {
        cause: attackerKit.plan.id === 'human' ? 'melee' : 'beast',
        battleId: `t${tick}`,
        attacker: { id: attacker.id, name: attacker.name },
        weapon: attackerKit.weapon.name,
        where: `${defender.x},${defender.y}`,
        narrative: `${attacker.name} struck ${defender.name}.`,
    };
    const damage = resolveDamage({
        rng,
        plan: defenderKit.plan,
        body: defenderC.vitals.body,
        wounds: defenderC.vitals.wounds,
        table,
        profile: attackerKit.profile,
        margin,
        damageRating: weaponDamageRating(attackerKit.weapon, attackerC.loadout.attributes, attackerC.loadout.edge),
        toughness: toughnessRating(defenderC.loadout.attributes),
        // Muscle-powered blows inherit the wielder's scale; fixed-power sources do
        // not. The two builds put that impact onto the target's scale before the
        // wound table resolves it and also drive the vital mass-ratio kill.
        attackerFrame: attackerKit.weapon.strengthScaled
            ? attackerC.loadout.attributes.frame
            : undefined,
        defenderFrame: defenderC.loadout.attributes.frame,
        targetingSkill: attackerC.loadout.skill,
        armour: defenderKit.armour,
        tick,
        ...(swing.part === null ? {} : { targetPart: swing.part }),
        woundId: `w${tick}:${attacker.id}>${defender.id}`,
        provenance,
    });
    // Refill before shock lands, so the stagger test is against the dice this
    // exchange actually had rather than whatever was left of the last one.
    defenderC.vitals.pool = dice.defence;
    const events = sufferBlow(rng, defenderKit.plan, defenderC.vitals, defenderC.loadout.attributes, damage, tick);
    // A stagger interrupts a wind-up and drops the guard — the opening aimed
    // blows want, and the reason two quick blows beat one big one at the same
    // total.
    if (defenderC.vitals.staggered) {
        defenderC.swing = null;
        defenderC.guarding = false;
    }
    // Being hit ends a stalk too, and for the same reason a stagger drops a
    // guard: whatever you were doing carefully, you are not doing it now.
    breakStalk(ctx, defender.id, defenderC, 'struck');
    if (defenderC.vitals.prone && defenderC.proneUntil === 0) {
        defenderC.proneUntil = tick + STAND_TICKS;
    }
    // Named whether it held or not: what turned the blow, or what the blow went
    // through on its way in. The second is what the clothing layer is for.
    const covering = armourAt(defenderKit.armour, damage.zone, attackerKit.profile.damageType);
    return {
        ...parties,
        defence: damage.turnedByArmour ? 'armour' : null,
        armour: covering?.name ?? null,
        damage,
        events,
    };
}
/* -------------------------------------------------------------------------- *
 * The systems
 * -------------------------------------------------------------------------- */
/** Everyone with a body, in a fixed order, so a replay cannot reorder a tick. */
function fighters(world) {
    return Object.keys(world.combat)
        .sort()
        .map((id) => world.actors[id])
        .filter((actor) => actor !== undefined);
}
/**
 * The tick, for everyone holding a weapon.
 *
 * Three passes rather than one loop, because the order *between* fighters must
 * not decide anything: every strike due this tick resolves before recovery and
 * upkeep, so two people who committed on the same tick land together rather
 * than the alphabetically earlier one staggering the other out of their swing.
 */
export const combatSystem = {
    name: 'combat',
    phase: 'sim',
    run(ctx) {
        const actors = fighters(ctx.world);
        for (const actor of actors)
            resolveDue(ctx, actor);
        for (const actor of actors)
            release(ctx, actor);
        for (const actor of actors)
            upkeep(ctx, actor);
    },
};
function resolveDue(ctx, attacker) {
    const combatant = ctx.world.combat[attacker.id];
    const swing = combatant?.swing;
    if (!combatant || !swing || swing.strikeAt !== ctx.tick)
        return;
    combatant.swing = null;
    combatant.recoveryUntil = ctx.tick + swing.recovery;
    // Footwork beats commitment: they moved, so there is no blow. Silent by
    // design — the strike never happened, and a log line saying it missed would
    // describe an event the sim did not have.
    if (attacker.x !== swing.fromX || attacker.y !== swing.fromY) {
        ctx.emit({
            type: 'attacked',
            actors: [attacker.id],
            refs: ['cancelled'],
            audience: 'archive',
            at: { x: attacker.x, y: attacker.y },
        });
        return;
    }
    const attackerKit = kitOf(ctx.catalogue, combatant);
    if (!attackerKit)
        return;
    const request = {
        targetId: swing.targetId,
        direction: swing.direction,
        part: swing.part,
    };
    // A ranged attack paid its arrow when it committed. Recheck position, target,
    // range and line of fire, but do not demand a *second* arrow at resolution —
    // otherwise the final shaft is consumed and then cancelled for leaving zero.
    const fault = attackFault(ctx.world, ctx.catalogue, attacker, request, ctx.tick, false, attackerKit.weapon.ranged !== undefined);
    const defender = fault === null ? resolveTarget(ctx.world, attacker, request) : null;
    const defenderC = defender ? ctx.world.combat[defender.id] : undefined;
    const defenderKit = defenderC ? kitOf(ctx.catalogue, defenderC) : null;
    if (!defender || !defenderC || !defenderKit) {
        // The target the player saw is gone, or was never reachable. Say so rather
        // than dropping it: M2-01's criterion is that an attack lands, or corrects
        // *visibly* — never silently misses.
        ctx.emit({
            type: 'blow',
            actors: [attacker.id],
            refs: ['air', fault ?? 'gone'],
            text: `${attacker.name} strikes at nothing.`,
            at: { x: attacker.x, y: attacker.y },
        });
        return;
    }
    const shieldBefore = defenderC.shieldWear;
    const blow = resolveBlow(ctx, attacker, attackerKit, defender, defenderKit, swing);
    if (!blow)
        return;
    ctx.emit({
        type: 'blow',
        actors: [attacker.id, defender.id],
        refs: refsFor(blow, swing),
        ...(narrate(ctx, blow) ?? {}),
        at: { x: defender.x, y: defender.y },
    });
    // After the blow it answered, because that is the order it happened in.
    if (defenderC.shieldWear !== shieldBefore) {
        const condition = shieldConditionAt(defenderC.shieldWear);
        ctx.emit({
            type: 'status',
            actors: [defender.id],
            refs: ['shield', condition],
            text: SHIELD_NARRATION[condition](defender.name),
            at: { x: defender.x, y: defender.y },
        });
    }
    for (const event of blow.events) {
        if (event.type !== 'died')
            continue;
        // Creature deaths become carcasses later in this same sim phase. That
        // system emits the canonical death with the species and credited killer;
        // emitting here as well produced two death lines for one animal and let
        // quest logic see an incomplete event first.
        if (defender.kind === 'creature')
            continue;
        ctx.emit({
            type: 'died',
            // Victim first, then whoever did it — the `died` convention, and the
            // reason it matters is M5-01: the chronicle reads the second actor to
            // know that a *wolf* took the cow rather than that the cow simply
            // stopped. A death with nobody on the other end of it is a death the
            // village cannot talk about.
            actors: [defender.id, attacker.id],
            refs: event.refs,
            text: `${defender.name} is dead.`,
            at: { x: defender.x, y: defender.y },
        });
    }
}
/**
 * What a shield going under sounds like.
 *
 * Written here rather than in the narration pools for the reason `winded` and
 * `stood-up` are: these are state changes the combat rules make outside the
 * blow chain, and a fighter whose world has no prose loaded should still be
 * told their shield has gone. `sound` never renders — a shield that has taken
 * no damage has nothing to say.
 */
const SHIELD_NARRATION = {
    sound: (name) => `${name}’s shield holds.`,
    notched: (name) => `${name}’s shield takes the blow and splinters at the rim.`,
    split: (name) => `${name}’s shield splits across the boss.`,
    broken: (name) => `${name}’s shield comes apart, and there is nothing left to hold up.`,
};
/** The tuple beside the prose: queryable long after the templates change. */
function refsFor(blow, swing) {
    return [
        blow.weapon,
        blow.attack,
        blow.defence ?? 'landed',
        String(blow.damage?.level ?? 0),
        blow.damage?.part.id ?? '',
        swing.part === null ? 'swung' : 'aimed',
    ];
}
/** Render the chain, if this world has any prose in it. */
function narrate(ctx, blow) {
    const pools = ctx.catalogue.narration;
    if (!pools)
        return null;
    // A stream of its own: whether the world has narration loaded must not shift
    // the dice a replay draws.
    const clauses = narrateBlow(ctx.rng('narration'), pools, blow);
    if (clauses.length === 0)
        return null;
    return { text: clauses.map((clause) => clause.text).join(' ') };
}
/** Recovery elapsing is not an event; it is just the tick moving on. */
function release(ctx, actor) {
    const combatant = ctx.world.combat[actor.id];
    if (!combatant)
        return;
    if (combatant.recoveryUntil !== 0 && combatant.recoveryUntil <= ctx.tick) {
        combatant.recoveryUntil = 0;
    }
}
/** Breath, standing up, and the defence window sliding forward. */
function upkeep(ctx, actor) {
    const combatant = ctx.world.combat[actor.id];
    if (!combatant)
        return;
    // A death, disconnect or corpse conversion can remove the other combatant
    // between exchanges. Do not leave the survivor permanently paired with an
    // id that no longer has a body.
    if (combatant.engagedWith !== null && !ctx.world.combat[combatant.engagedWith]) {
        combatant.engagedWith = null;
        combatant.withdrawingUntil = 0;
    }
    if (combatant.engagedWith === null) {
        const attacker = engagedAttackers(ctx.world, actor.id)[0];
        if (attacker)
            combatant.engagedWith = attacker;
    }
    const kit = kitOf(ctx.catalogue, combatant);
    if (!kit)
        return;
    // A body that has stopped answering drops what it was holding on to (M2-07).
    //
    // `attackFault` already refuses to *land* a senseless fighter's blow, so this
    // is not about damage — it is about the state left behind. Without it a
    // corpse keeps a wind-up that resolves into a "strikes at nothing" line a tick
    // later, keeps a guard that discounts blows aimed at it, and keeps a buffered
    // attack that fires the instant somebody staunches them awake.
    //
    // Here rather than in `die` for the reason `carcassSystem` watches the table
    // rather than the event: this catches going down from blood loss, from a head
    // wound, from shock and from every future way of losing consciousness,
    // without any of them having to remember to say so.
    if (combatant.vitals.dead || isDown(kit.plan, combatant.vitals, ctx.tick)) {
        combatant.swing = null;
        combatant.queued = null;
        combatant.guarding = false;
        combatant.sneaking = false;
        combatant.channel = null;
    }
    pruneDefences(combatant, ctx.tick);
    if (combatant.riposteUntil !== 0 && ctx.tick >= combatant.riposteUntil) {
        combatant.riposteAgainst = null;
        combatant.riposteUntil = 0;
    }
    if (combatant.withdrawingUntil !== 0 && ctx.tick >= combatant.withdrawingUntil) {
        const opponent = combatant.engagedWith ? ctx.world.combat[combatant.engagedWith] : undefined;
        const replacement = combatant.engagedWith
            ? (engagedAttackers(ctx.world, combatant.engagedWith).find((id) => id !== actor.id) ?? null)
            : null;
        for (const other of Object.values(ctx.world.combat)) {
            if (other.engagedWith === actor.id)
                other.engagedWith = null;
        }
        if (opponent?.engagedWith === actor.id)
            opponent.engagedWith = replacement;
        combatant.engagedWith = null;
        combatant.withdrawingUntil = 0;
        ctx.emit({
            type: 'status',
            actors: [actor.id],
            refs: ['disengaged'],
            text: `${actor.name} gets clear.`,
            at: { x: actor.x, y: actor.y },
        });
    }
    if (!isCommitted(combatant, ctx.tick) && !combatant.vitals.dead) {
        const regen = combatant.guarding ? STAMINA_REGEN * GUARD_REGEN_FACTOR : STAMINA_REGEN;
        const full = maxStamina(kit.plan, combatant.vitals, combatant.loadout.attributes);
        combatant.staminaMilli = Math.min(full * STAMINA_SCALE, combatant.staminaMilli + Math.round(regen));
    }
    // Announced on the way in only. "Gwil is winded" is a thing the log should
    // say once, not every tick he spends under a third of his wind — and it is
    // compared against the last announcement rather than against the last tick,
    // because the drops that matter come from swinging, not from breathing.
    const band = bandAt(staminaFraction(combatant, kit)).id;
    if (band !== combatant.band) {
        if (band === 'winded') {
            ctx.emit({
                type: 'status',
                actors: [actor.id],
                refs: ['winded'],
                text: `${actor.name} is winded.`,
                at: { x: actor.x, y: actor.y },
            });
        }
        combatant.band = band;
    }
    if (combatant.vitals.prone &&
        combatant.proneUntil !== 0 &&
        ctx.tick >= combatant.proneUntil &&
        !isDown(kit.plan, combatant.vitals, ctx.tick)) {
        combatant.vitals.prone = false;
        combatant.proneUntil = 0;
        spendStamina(combatant, kit, STAND_COST);
        ctx.emit({
            type: 'status',
            actors: [actor.id],
            refs: ['stood-up'],
            text: `${actor.name} gets up.`,
            at: { x: actor.x, y: actor.y },
        });
    }
}
/**
 * The wound clocks, on the world's own tick (M1-04).
 *
 * Registered here rather than in `vitals.ts` because a clock needs bodies to
 * run over, and bodies only arrived on actors with M2. Bleeding accrues per
 * tick rather than per game hour for the reason M1-04 gives: a wound that only
 * bled on the hour boundary would make first aid a race against a clock the
 * player cannot see.
 */
export const woundClockSystem = {
    name: 'wound-clocks',
    phase: 'sim',
    run(ctx) {
        for (const actor of fighters(ctx.world)) {
            const combatant = ctx.world.combat[actor.id];
            for (const event of advanceClocks(combatant.vitals, ctx.tick)) {
                ctx.emit({
                    type: event.type === 'died' ? 'died' : 'status',
                    actors: [actor.id],
                    refs: [event.type, ...event.refs],
                    text: textFor(actor, event),
                    at: { x: actor.x, y: actor.y },
                });
            }
        }
    },
};
function textFor(actor, event) {
    switch (event.type) {
        case 'died':
            return `${actor.name} is dead.`;
        case 'roused':
            return `${actor.name} stirs.`;
        case 'blood-stage':
            return `${actor.name} is ${event.refs[0] ?? 'failing'}.`;
        default:
            return `${actor.name}: ${event.type}.`;
    }
}
/** Everything M2-01 through M2-03 install on the rule set. */
export const COMBAT_SYSTEMS = [combatSystem, woundClockSystem];
//# sourceMappingURL=combat.js.map